// src/components/WalletLoading.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import walletLogo from '../assets/wallet-logo.png';

/**
 * WalletLoading component - shows a loading screen with a specific message
 * 
 * @param {string} message - The message to display. If null, default welcome message is used.
 * @param {number} minLoadTime - Minimum time to show the loading screen in ms
 */
const WalletLoading = ({ message = null, minLoadTime = 1000 }) => {
  const { t } = useTranslation();
  // State to control visibility based on minimum load time
  const [canDismiss, setCanDismiss] = useState(false);
  
  // Determine message to show - use prop if provided, otherwise use default translation
  const displayMessage = message || t('welcome.steps.welcomeIntro');
  
  // Effect to enforce minimum display time
  useEffect(() => {
    // Set a timeout to allow dismissal after minLoadTime
    const timer = setTimeout(() => {
      setCanDismiss(true);
    }, minLoadTime);
    
    // Important: Make the component immediately visible
    document.body.style.overflow = 'hidden';
    
    // Cleanup function
    return () => {
      clearTimeout(timer);
      document.body.style.overflow = '';
    };
  }, [minLoadTime]);
  
  // Export canDismiss state so parent components can check if minimum time has passed
  if (typeof window !== 'undefined') {
    window.walletLoadingCanDismiss = canDismiss;
  }
  
  return (
    <div className="wallet-loading-container" data-can-dismiss={canDismiss.toString()}>
      <div className="logo-container-loading">
        <img src={walletLogo} alt={t('app.name')} className="wallet-logo-loading" />
        <div className="pulse-ring"></div>
      </div>
      
      <h2 className="wallet-loading-title">{displayMessage}</h2>
      
      <div className="loading-dots">
        <span></span>
        <span></span>
        <span></span>
      </div>
      
      <style jsx="true">{`
        .wallet-loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100vh;
          background-color: var(--app-background, #121212);
          color: var(--text-primary, #f8f8f8);
          text-align: center;
          padding: 20px;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          z-index: 9999;
          /* Prevent text selection during loading */
          user-select: none;
          -webkit-user-select: none;
          /* Force the screen to be visible */
          opacity: 1 !important;
          visibility: visible !important;
        }
        
        .logo-container-loading {
          position: relative;
          margin-bottom: 40px; /* Increased space to prevent overlap */
        }
        
        .wallet-logo-loading {
          width: 90px;
          height: 90px;
          object-fit: contain;
          position: relative;
          z-index: 2;
          animation: float 3s ease-in-out infinite;
        }
        
        .pulse-ring {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 100px;
          height: 100px;
          border-radius: 50%;
          background: radial-gradient(circle, 
            rgba(var(--primary-rgb), 0.4) 0%, 
            rgba(var(--primary-rgb), 0.1) 65%, 
            rgba(var(--primary-rgb), 0) 100%);
          z-index: 1;
          animation: pulse 2s ease-in-out infinite;
        }
        
        .wallet-loading-title {
          font-size: 1.6rem;
          margin-bottom: 30px; /* Increased space to prevent overlap */
          font-weight: 600;
          min-height: 2.5rem; /* Fixed height to prevent jumps */
          background: var(--primary-gradient);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          width: 100%;
          max-width: 90%; /* Increased from 280px to use percentage */
          padding: 0 15px;
          /* CHANGES: Removed nowrap, overflow hidden, and ellipsis */
          /* Added proper word wrapping */
          white-space: normal;
          word-wrap: break-word;
          text-align: center;
          line-height: 1.3;
        }
        
        .loading-dots {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          height: 30px;
          margin-top: 10px; /* Added space above dots */
        }
        
        .loading-dots span {
          display: inline-block;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background-color: var(--primary);
          opacity: 0.6;
        }
        
        .loading-dots span:nth-child(1) {
          animation: dot-animation 1.4s infinite ease-in-out;
        }
        
        .loading-dots span:nth-child(2) {
          animation: dot-animation 1.4s infinite ease-in-out 0.2s;
        }
        
        .loading-dots span:nth-child(3) {
          animation: dot-animation 1.4s infinite ease-in-out 0.4s;
        }
        
        @keyframes dot-animation {
          0%, 100% {
            transform: scale(1);
            opacity: 0.6;
          }
          50% {
            transform: scale(1.5);
            opacity: 1;
          }
        }
        
        @keyframes pulse {
          0% {
            transform: translate(-50%, -50%) scale(0.8);
            opacity: 0.8;
          }
          70% {
            transform: translate(-50%, -50%) scale(1.2);
            opacity: 0.2;
          }
          100% {
            transform: translate(-50%, -50%) scale(0.8);
            opacity: 0.8;
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }
        
        /* Media query for smaller screens */
        @media (max-width: 480px) {
          .wallet-logo-loading {
            width: 75px;
            height: 75px;
          }
          
          .pulse-ring {
            width: 85px;
            height: 85px;
          }
          
          .wallet-loading-title {
            font-size: 1.4rem;
            max-width: 95%;
          }
        }
        
        /* Theme-specific adjustments */
        [data-theme="light"] .pulse-ring {
          background: radial-gradient(circle, 
            rgba(var(--primary-rgb), 0.3) 0%, 
            rgba(var(--primary-rgb), 0.1) 65%, 
            rgba(var(--primary-rgb), 0) 100%);
        }
        
        /* Extra styles to ensure loading screen is always on top and blocks all interactions */
        .wallet-loading-container {
          isolation: isolate;
          pointer-events: all;
          touch-action: none;
        }
      `}</style>
    </div>
  );
};

export default WalletLoading;